import { localStorage as safeLocalStorage } from 'utils/browserStorage';
import { useCallback, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import Api from '../../../../api';
import { shouldBlockAvailableNowByMembership } from '../../../../utils/membership';
import { HygienistProps, MembershipStatusResponse } from '../../../../utils/types';

interface UseAvailableNowOptions {
  membership?: MembershipStatusResponse | null;
  membershipLoading?: boolean;
}

interface UseAvailableNowReturn {
  isAvailable: boolean;
  isBlocked: boolean;
  isMembershipBlocked: boolean;
  blockedDaysRemaining: number;
  isLoading: boolean;
  toggle: (next: boolean) => Promise<void>;
  showInfo: () => void;
  // Expose modal states + controls for React replacement dialogs
  ui: {
    infoOpen: boolean;
    setInfoOpen: (v: boolean) => void;
    penaltyOpen: boolean;
    setPenaltyOpen: (v: boolean) => void;
    promptOpen: boolean;
    setPromptOpen: (v: boolean) => void;
    promptDateKey: string;
  }
}

/**
 * Manage "Available Today" toggle state with penalty guard and info modal.
 */
export default function useAvailableNow(options: UseAvailableNowOptions = {}): UseAvailableNowReturn {
  const { membership = null, membershipLoading = false } = options;
  const [hygienist, setHygienist] = useState<HygienistProps | null>(null);
  const [isAvailable, setIsAvailable] = useState<boolean>(false);
  const [isBlocked, setIsBlocked] = useState<boolean>(false);
  const [blockedDaysRemaining, setBlockedDaysRemaining] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  // React dialog states
  const [infoOpen, setInfoOpen] = useState<boolean>(false);
  const [penaltyOpen, setPenaltyOpen] = useState<boolean>(false);
  const [promptOpen, setPromptOpen] = useState<boolean>(false);
  const [promptDateKey, setPromptDateKey] = useState<string>('');
  const isMembershipBlocked = shouldBlockAvailableNowByMembership(membership);

  const computeBlocked = useCallback((blockedUntil: number | null) => {
    // FFS-only release direction:
    // legacy blockedUntil should no longer drive user-facing enforcement on refactored surfaces.
    void blockedUntil;
    setIsBlocked(false);
    setBlockedDaysRemaining(0);
  }, []);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const user = await Api.getUser();
      const hyg = user?.hygienist || null;
      setHygienist(hyg);
      setIsAvailable(!!hyg?.AvailableNow);
      computeBlocked(hyg?.blockedUntil ?? null);
    } finally {
      setIsLoading(false);
    }
  }, [computeBlocked]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (isMembershipBlocked) {
      setIsAvailable(false);
    }
  }, [isMembershipBlocked]);

  const showInfo = useCallback(() => {
    setInfoOpen(true);
  }, []);

  const toggle = useCallback(async (next: boolean) => {
    if (!hygienist) return;
    if (next && (membershipLoading || isMembershipBlocked)) {
      setIsAvailable(false);
      return;
    }
    if (next) {
      if (isBlocked) {
        setPenaltyOpen(true);
        setIsAvailable(false);
        return;
      }
    }

    setIsLoading(true);
    try {
      await Api.updateHygienist({ AvailableNow: next ? 1 : 0 });
      const user = await Api.getUser();
      setHygienist(user?.hygienist || null);
      setIsAvailable(!!user?.hygienist?.AvailableNow);
      computeBlocked(user?.hygienist?.blockedUntil ?? null);

      if (next) {
        const seen = safeLocalStorage.getItem('availableNoShow') === 'true';
        if (!seen) setInfoOpen(true);

        try {
          const todayKey = dayjs().format('YYYY-MM-DD');
          const type = (user?.hygienist?.type ?? 0) as number;
          const todays = await Api.getAvailableJobs(type, todayKey);
          if (Array.isArray(todays) && todays.length > 0) {
            setPromptDateKey(todayKey);
            setPromptOpen(true);
          }
        } catch {}
      }
    } finally {
      setIsLoading(false);
    }
  }, [hygienist, membershipLoading, isMembershipBlocked, isBlocked, blockedDaysRemaining, computeBlocked, showInfo]);

  return {
    isAvailable,
    isBlocked,
    isMembershipBlocked,
    blockedDaysRemaining,
    isLoading,
    toggle,
    showInfo,
    ui: {
      infoOpen,
      setInfoOpen,
      penaltyOpen,
      setPenaltyOpen,
      promptOpen,
      setPromptOpen,
      promptDateKey,
    }
  };
}


